import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { ArrowPathIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import AuthLayout from './components/AuthLayout';
import { useRegister, resendVerificationEmail } from './api/auth';
import { ValidationErrors } from './types';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export default function Register() {
  useDocumentTitle('Create Account', 'Create your free account and start finding decision makers today.');
  const { mutateAsync: register, isPending } = useRegister();

  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [verificationSent, setVerificationSent] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    try {
      await register(formData);
      setVerificationSent(formData.email);
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string; errors: ValidationErrors }>;
      if (axiosError.response?.status === 422) {
        setErrors(axiosError.response.data.errors || {});
        toast.error(axiosError.response.data.message || 'Please check the form for errors.');
      }
    }
  };

  const handleResend = async () => {
    if (!verificationSent) return;
    setIsResending(true);
    try {
      await resendVerificationEmail(verificationSent);
      toast.success('Verification email resent. Please check your inbox.');
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      if (axiosError.response?.status === 422) {
        toast.info(axiosError.response.data.message || 'Email is already verified.');
      } else {
        toast.error('Failed to resend. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  // Password strength
  const passwordStrength = (() => {
    const p = formData.password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (p.length >= 10 && /[A-Z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();

  const strengthColors = ['', 'bg-red-400', 'bg-amber-400', 'bg-brand-500', 'bg-emerald-500'];
  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthTextColors = ['', 'text-red-500', 'text-amber-500', 'text-brand-700', 'text-emerald-600'];

  // ── Verification sent state ──────────────────────────────────────────────────
  if (verificationSent) {
    return (
      <AuthLayout mode="register" title="" subtitle="">
        <div className="text-center">
          <div className="w-20 h-20 bg-brand-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>

          <h2 className="text-2xl font-extrabold text-slate-900 mb-2 tracking-tight">Check your inbox</h2>
          <p className="text-sm text-slate-500 leading-relaxed mb-1">We sent a verification link to</p>
          <p className="text-sm font-bold text-slate-900 mb-4">{verificationSent}</p>

          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-7 text-left">
            <p className="text-xs text-slate-500 leading-relaxed">
              Click the link in the email to activate your account.
              If you don't see it, check your spam or junk folder.
            </p>
          </div>

          <button
            onClick={handleResend}
            disabled={isResending}
            className="flex items-center gap-2 mx-auto text-sm font-semibold text-brand-700 hover:text-brand-900 transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
            {isResending ? 'Sending...' : 'Resend verification email'}
          </button>

          <div className="mt-8 pt-6 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              Already verified?{' '}
              <Link to="/login" className="font-bold text-brand-700 hover:text-brand-900 transition-colors">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // ── Registration form ────────────────────────────────────────────────────────
  return (
    <AuthLayout mode="register" title="Create your account" subtitle="Start finding verified leads in minutes.">
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Full Name */}
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
            Full Name
          </label>
          <input
            type="text"
            placeholder="John Doe"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={`w-full px-4 py-3.5 bg-white border rounded-xl text-sm text-slate-900 placeholder-slate-400 transition-all outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 ${
              errors.name ? 'border-red-400' : 'border-slate-200 hover:border-slate-300'
            }`}
          />
          {errors.name?.[0] && (
            <p className="mt-1.5 text-xs font-medium text-red-500">{errors.name[0]}</p>
          )}
        </div>

        {/* Work Email */}
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
            Work Email
          </label>
          <input
            type="email"
            placeholder="you@company.com"
            required
            value={formData.email}
            onChange={(e) => {
              setFormData({ ...formData, email: e.target.value });
              if (errors.email) setErrors((prev) => { const e = { ...prev }; delete e.email; return e; });
            }}
            className={`w-full px-4 py-3.5 bg-white border rounded-xl text-sm text-slate-900 placeholder-slate-400 transition-all outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 ${
              errors.email ? 'border-red-400 focus:ring-red-500/20 focus:border-red-400' : 'border-slate-200 hover:border-slate-300'
            }`}
          />
          {errors.email?.[0] && (
            <p className="mt-1.5 text-xs font-medium text-red-500">{errors.email[0]}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className={`w-full px-4 py-3.5 pr-11 bg-white border rounded-xl text-sm text-slate-900 placeholder-slate-400 transition-all outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500 ${
                errors.password ? 'border-red-400' : 'border-slate-200 hover:border-slate-300'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
            </button>
          </div>
          {errors.password?.[0] && (
            <p className="mt-1.5 text-xs font-medium text-red-500">{errors.password[0]}</p>
          )}

          {/* Password strength bar */}
          {formData.password && (
            <div className="mt-2.5">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4].map((level) => (
                  <div
                    key={level}
                    className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                      level <= passwordStrength ? strengthColors[passwordStrength] : 'bg-slate-200'
                    }`}
                  />
                ))}
              </div>
              <p className={`text-xs font-semibold ${strengthTextColors[passwordStrength]}`}>
                {strengthLabels[passwordStrength]} password
              </p>
            </div>
          )}
        </div>

        {/* Terms */}
        <p className="text-xs text-slate-400 leading-relaxed">
          By creating an account, you agree to our{' '}
          <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-700 hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-700 hover:underline">Privacy Policy</Link>.
        </p>

        {/* Submit */}
        <button
          type="submit"
          disabled={isPending}
          className="w-full bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-slate-900 font-bold py-3.5 rounded-xl text-sm transition-all shadow-sm hover:shadow-md active:scale-[0.99]"
        >
          {isPending ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Creating account…
            </span>
          ) : 'Create account'}
        </button>
      </form>

    </AuthLayout>
  );
}
